import { getSrc } from '@/services/media.js'
import { buildRendererPropsFromItem } from '@/utils/dsItemRendererProps.js'
import { resolveWallCollisions } from '@/utils/designStudioCollision.js'

export const PROJECT_WALL_ITEM_LIMIT = 500
export const PROJECT_WALL_GAP_INCHES = 36
export const PROJECT_WALL_SIDE_PADDING_INCHES = 240
export const PROJECT_WALL_MIN_WIDTH_INCHES = 32 * 12
export const PROJECT_WALL_EYE_LEVEL_INCHES = 57

// Wall furniture for a workspace-grouped wall: a gallery title card opening each
// workspace, and a plant standing at its first piece.
//
// Only the card takes a slot of its own — it hangs on the wall, so the layout
// has to leave it room. The plant stands on the floor and takes no wall, so it
// claims no slot and instead stands at the first piece of the run it opens:
//
//   [card] [plant + piece 1] [piece 2] … [card] [plant + piece 3] [piece 4] …
export const WORKSPACE_CARD_WIDTH_INCHES = 24
export const WORKSPACE_CARD_HEIGHT_INCHES = 16
export const WORKSPACE_PLANT_HEIGHT_INCHES = 48
export const WORKSPACE_MARKER_KIND_CARD = 'workspace-card'
export const WORKSPACE_MARKER_KIND_PLANT = 'workspace-plant'

function normalizeId(value) {
  if (value == null || value === '') return null
  return String(value)
}

function projectItemKey(item) {
  return `project-item:${item?.id ?? 'unknown'}`
}

function parsePositiveNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

// Wall furniture carries an explicit footprint instead of renderer props: it is
// the slot the layout has to reserve, not a piece with dimensions to derive.
export function isWallMarkerSpec(spec) {
  return Boolean(spec?.marker)
}


/**
 * Floor furniture: it stands at another spec's position and reserves no wall of
 * its own. `marker.anchorKey` names the spec it stands at.
 */
export function isFloatingMarkerSpec(spec) {
  return Boolean(spec?.marker?.floating)
}

function getRenderItemWidth(renderItem) {
  if (isFloatingMarkerSpec(renderItem)) return 0
  const reserved = parsePositiveNumber(renderItem?.footprint?.width)
  if (reserved > 0) return reserved
  const props = renderItem?.rendererProps || {}
  return parsePositiveNumber(props.targetWidth, parsePositiveNumber(props.artWidth, 16))
}

function getRenderItemHeight(renderItem) {
  if (isFloatingMarkerSpec(renderItem)) return 0
  const reserved = parsePositiveNumber(renderItem?.footprint?.height)
  if (reserved > 0) return reserved
  const props = renderItem?.rendererProps || {}
  return parsePositiveNumber(props.targetHeight, parsePositiveNumber(props.artHeight, 16))
}

// Push overlapping items apart in place (settle/commit mode) so the resolved layout has no
// overlaps, keeping the selected item(s) pinned. `pinnedItemId` accepts a single id or an
// array (multi-item drags pin the whole moved group). Operates on the wall-relative
// position.x/y the items already carry, and returns a new array with updated positions
// (footprints stay put).
function separateWallRenderItems(resolvedItems, { pinnedKey = null, pinnedItemId = null } = {}) {
  if (!Array.isArray(resolvedItems) || resolvedItems.length < 2) return resolvedItems

  const normalizedPinnedKey = pinnedKey == null ? null : String(pinnedKey)
  const pinnedIds = new Set(
    (Array.isArray(pinnedItemId) ? pinnedItemId : [pinnedItemId])
      .map(normalizeId)
      .filter(id => id != null)
  )

  // Floor furniture stands at a piece rather than beside it, so handing it to
  // the solver would have it shoving the very piece it belongs to.
  const collidable = resolvedItems.filter(item => !isFloatingMarkerSpec(item))
  if (collidable.length < 2) return resolvedItems

  const boxes = collidable.map((item) => {
    const x = Number(item?.position?.x) || 0
    const y = Number(item?.position?.y) || 0
    const itemId = normalizeId(item?.itemId)
    // Wall furniture is structure, not content: it holds its slot and the art
    // moves around it, never the other way round.
    const pinned = isWallMarkerSpec(item)
      || (normalizedPinnedKey != null && String(item?.key) === normalizedPinnedKey)
      || pinnedIds.has(itemId)
    return {
      key: item?.key,
      x,
      y,
      halfW: getRenderItemWidth(item) / 2,
      halfH: getRenderItemHeight(item) / 2,
      pinned,
      anchorX: x,
      anchorY: y,
    }
  })

  resolveWallCollisions(boxes) // settle mode (full separation, no gravity)

  const byKey = new Map(boxes.map(box => [String(box.key), box]))
  return resolvedItems.map((item) => {
    const box = byKey.get(String(item?.key))
    if (!box) return item
    if (box.x === item.position?.x && box.y === item.position?.y) return item
    return { ...item, position: { ...item.position, x: box.x, y: box.y } }
  })
}

export function getProjectWallLayoutFromSceneXs(
  sceneXs = [],
  minWallWidth = PROJECT_WALL_MIN_WIDTH_INCHES,
) {
  const xs = (Array.isArray(sceneXs) ? sceneXs : [])
    .map(value => Number(value))
    .filter(Number.isFinite)

  if (!xs.length) {
    const wallWidth = parsePositiveNumber(minWallWidth, PROJECT_WALL_MIN_WIDTH_INCHES)
    return {
      wallWidth,
      wallCenterX: 0,
      wallLeftX: -wallWidth / 2,
    }
  }

  const firstX = xs[0]
  const lastX = xs[xs.length - 1]
  const span = Math.abs(lastX - firstX)
  const wallWidth = Math.max(
    parsePositiveNumber(minWallWidth, PROJECT_WALL_MIN_WIDTH_INCHES),
    span + PROJECT_WALL_SIDE_PADDING_INCHES * 2,
  )
  const wallCenterX = (firstX + lastX) / 2
  return {
    wallWidth,
    wallCenterX,
    wallLeftX: wallCenterX - wallWidth / 2,
  }
}

function applyWallPositions(renderItems, gapInches = PROJECT_WALL_GAP_INCHES) {
  const gap = parsePositiveNumber(gapInches, PROJECT_WALL_GAP_INCHES)

  // Compute the natural auto-layout position for every item as if nothing were pinned.
  // This keeps unpinned items stable when other items are moved.
  //
  // Floor furniture is skipped here and placed below: it hangs on nothing, so
  // letting it advance the cursor would open a stretch of empty wall for an
  // object that needs none.
  let cursor = 0
  let placedAny = false
  const naturalX = renderItems.map((renderItem) => {
    if (isFloatingMarkerSpec(renderItem)) return null
    const width = getRenderItemWidth(renderItem)
    const x = placedAny ? cursor + gap + (width / 2) : 0
    cursor = x + (width / 2)
    placedAny = true
    return x
  })
  const wallLayout = getProjectWallLayoutFromSceneXs(naturalX.filter(x => x !== null))

  const positioned = renderItems.map((renderItem, index) => {
    if (renderItem.wallPositionSet) {
      return { ...renderItem, naturalX: naturalX[index], wallSpacePosition: true }
    }
    return {
      ...renderItem,
      naturalX: naturalX[index],
      wallSpacePosition: true,
      position: {
        ...renderItem.position,
        x: naturalX[index] - wallLayout.wallLeftX,
        y: PROJECT_WALL_EYE_LEVEL_INCHES,
        z: 0,
      },
    }
  })

  // Floor furniture stands where the piece it is anchored to ended up — which
  // is that piece's saved position when it has one, so this has to read the
  // placed result rather than the natural layout it may have overridden.
  const byKey = new Map(positioned.map(spec => [String(spec.key), spec]))
  return positioned.map((spec) => {
    if (!isFloatingMarkerSpec(spec)) return spec
    const anchor = byKey.get(String(spec.marker.anchorKey))
    if (!anchor) return spec
    return {
      ...spec,
      naturalX: anchor.naturalX,
      position: { ...spec.position, x: anchor.position?.x ?? spec.position?.x },
    }
  })
}

const SELECTED_AFFORDANCES = { resize: true, source: true, dimensions: true, snapOverlay: true }

export function buildSavedRenderItem({
  item,
  active = false,
  selected = active,
  affordances = null,
  availableOptions = [],
} = {}) {
  if (!item) return null
  const rendererProps = buildRendererPropsFromItem(item, availableOptions)
  const finishedWidth = Number(item.width)
  const finishedHeight = Number(item.height)

  const wallX = item.config_data?.wall_offset_x
  const wallY = item.config_data?.wall_offset_y
  const wallPositionSet = Number.isFinite(Number(wallX)) && Number.isFinite(Number(wallY))

  return {
    key: projectItemKey(item),
    itemId: item.id,
    workspace: normalizeWorkspaceName(item),
    selected: Boolean(selected),
    // Compatibility for older call sites/tests while FrameRenderer moves to selected specs.
    active: Boolean(selected),
    affordances: affordances || {},
    position: {
      x: wallPositionSet ? Number(wallX) : 0,
      y: wallPositionSet ? Number(wallY) : 0,
      z: 0,
    },
    wallPositionSet,
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    rendererProps: {
      ...rendererProps,
      targetWidth: Number.isFinite(finishedWidth) && finishedWidth > 0 ? finishedWidth : null,
      targetHeight: Number.isFinite(finishedHeight) && finishedHeight > 0 ? finishedHeight : null,
    },
    thumbnailUrl: item.thumbnail || getSrc(item.imagedata, 'imgsrc') || getSrc(item.imagedata) || null,
  }
}

export function buildLiveRenderItem({
  itemId,
  key,
  rendererProps,
  active = true,
  selected = active,
  affordances = null,
} = {}) {
  if (!rendererProps) return null
  return {
    key: key || `live-item:${itemId ?? 'new'}`,
    itemId,
    selected: Boolean(selected),
    active: Boolean(selected),
    affordances: affordances || {},
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    rendererProps: { ...rendererProps },
  }
}

export function normalizeWorkspaceName(item) {
  return String(item?.workspace ?? '').trim()
}

export function workspaceDisplayLabel(workspace) {
  return String(workspace || '').trim() || 'No workspace'
}

/**
 * Wall order for a workspace-grouped wall.
 *
 * Workspaces run in the order they first appear in the incoming sort, and each
 * one's items keep their relative order within it. Grouping is what makes a
 * "boundary" mean anything: interleaved workspaces have no break to stand a
 * plant in, and no run for a title card to open.
 */
export function groupItemsByWorkspace(items = []) {
  const list = Array.isArray(items) ? items : []
  const runs = new Map()
  list.forEach((item) => {
    const workspace = normalizeWorkspaceName(item)
    if (!runs.has(workspace)) runs.set(workspace, [])
    runs.get(workspace).push(item)
  })
  return [].concat(...runs.values())
}

function buildWorkspaceMarker(kind, workspace, { width, height, anchorKey = null }) {
  return {
    key: `${kind}:${workspace}`,
    itemId: null,
    marker: {
      kind,
      workspace,
      label: workspaceDisplayLabel(workspace),
      ...(anchorKey == null ? {} : { floating: true, anchorKey }),
    },
    footprint: { width, height },
    selected: false,
    active: false,
    affordances: {},
    position: { x: 0, y: 0, z: 0 },
    wallPositionSet: false,
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
    rendererProps: null,
  }
}

export function buildProjectWallRenderItems({
  projectItems = [],
  availableOptions = [],
  limit = PROJECT_WALL_ITEM_LIMIT,
  groupByWorkspace = false,
} = {}) {
  if (!Array.isArray(projectItems) || !projectItems.length) return []

  const selected = projectItems.slice(0, limit)
  const toSpec = item => buildSavedRenderItem({
    item,
    selected: false,
    affordances: {},
    availableOptions,
  })

  const specs = selected.map(toSpec).filter(Boolean)
  return groupByWorkspace ? addWorkspaceMarkers(specs) : specs
}

function addWorkspaceMarkers(items) {
  const ordered = groupItemsByWorkspace(items)
  const workspaces = [...new Set(ordered.map(normalizeWorkspaceName))]
  // One workspace is not a grouping — there is nothing to divide, and a lone
  // card announcing the only run on the wall is just clutter.
  if (workspaces.length < 2) return ordered

  // Per workspace: the card in a slot of its own, then the run's pieces, with the
  // plant standing at the first of them.
  const specs = []
  let currentWorkspace = null
  ordered.forEach((spec) => {
    const workspace = normalizeWorkspaceName(spec)
    if (workspace !== currentWorkspace) {
      const opensABoundary = currentWorkspace !== null
      specs.push(buildWorkspaceMarker(WORKSPACE_MARKER_KIND_CARD, workspace, {
        width: WORKSPACE_CARD_WIDTH_INCHES,
        height: WORKSPACE_CARD_HEIGHT_INCHES,
      }))
      // The first workspace opens the wall rather than breaking it, so it gets a
      // card but no plant: there is no run before it to divide it from.
      if (opensABoundary) {
        specs.push(buildWorkspaceMarker(WORKSPACE_MARKER_KIND_PLANT, workspace, {
          width: 0,
          height: WORKSPACE_PLANT_HEIGHT_INCHES,
          anchorKey: spec.key,
        }))
      }
      currentWorkspace = workspace
    }
    specs.push(spec)
  })
  return specs
}

export function resolveProjectWallRenderItems({
  renderItems = [],
  selectedItemId = null,
  // 2+ ids switch to multi-selection: every matching spec is marked selected,
  // but interaction affordances and live renderer props only ever apply to a
  // single selection. 0-1 ids fall back to legacy single-id behavior.
  selectedItemIds = null,
  selectedRendererItemId = null,
  selectedRendererProps = null,
  gapInches = PROJECT_WALL_GAP_INCHES,
  layoutMode = 'free',
  layoutAnchor = null,
  collisionEnabled = false,
  pinnedItemId = undefined,
} = {}) {
  const selectedId = normalizeId(selectedItemId)
  const multiIds = new Set(
    (Array.isArray(selectedItemIds) ? selectedItemIds : [])
      .map(normalizeId)
      .filter(id => id != null)
  )
  const multi = multiIds.size >= 2
  const selectedRendererId = normalizeId(selectedRendererItemId)
  // Selection is only viewer state; it must not decide collision layout. Pinning is reserved for
  // explicit layout commits, such as holding a just-moved item where the user dropped it.
  const pinnedId = pinnedItemId === undefined ? null : pinnedItemId
  const baseItems = Array.isArray(renderItems)
    ? renderItems.filter(item => item && (item.rendererProps || isWallMarkerSpec(item)))
    : []

  if (!baseItems.length) {
    const live = buildLiveRenderItem({
      itemId: selectedItemId,
      rendererProps: selectedRendererProps,
      selected: true,
      affordances: { ...SELECTED_AFFORDANCES },
    })
    return live ? applyWallPositions([live], gapInches) : []
  }

  // Position the stable scene before layering live selected-item props over it.
  // Otherwise a width preview participates in auto-layout and changes the
  // selected item's center by half the width delta, which makes the preview
  // look left-edge anchored. The live footprint is still used below for
  // collision resolution; only its center comes from the pre-preview scene.
  const positionedBaseItems = applyWallPositions(baseItems, gapInches)
  const resolvedItems = positionedBaseItems.map(item => {
    const itemId = normalizeId(item.itemId)
    const isSelected = multi
      ? multiIds.has(itemId)
      : (selectedId != null && itemId === selectedId)
    const useLiveProps = Boolean(
      !multi
      && isSelected
      && selectedRendererProps
      && selectedRendererId != null
      && selectedRendererId === itemId
    )

    if (isWallMarkerSpec(item)) return item

    return {
      ...item,
      selected: isSelected,
      active: isSelected,
      affordances: isSelected && !multi ? { ...SELECTED_AFFORDANCES } : {},
      rendererProps: useLiveProps ? { ...selectedRendererProps } : item.rendererProps,
    }
  })

  if (layoutMode === 'auto') {
    const row = applyWallPositions(resolvedItems.map(item => ({ ...item, wallPositionSet: false })), gapInches)
    const anchor = row.find(item => normalizeId(item.itemId) === normalizeId(layoutAnchor?.itemId))
    const offset = layoutAnchor?.offset ?? (anchor && layoutAnchor ? layoutAnchor.x - anchor.position.x : 0)
    return row.map(item => ({ ...item, position: { ...item.position, x: item.position.x + offset } }))
  }

  if (!collisionEnabled) return resolvedItems
  return separateWallRenderItems(resolvedItems, { pinnedItemId: pinnedId })
}

export function getActiveRenderItem(renderItems = []) {
  return getSelectedRenderItem(renderItems)
}

export function getSelectedRenderItem(renderItems = []) {
  return (Array.isArray(renderItems) ? renderItems : []).find(item => item?.selected || item?.active) || null
}

/** Move one item slot, adopting the neighboring item's workspace. */
export function stepAutoReorder(renderItems, itemId, direction) {
  const items = renderItems.filter(spec => spec.itemId != null)
  const index = items.findIndex(spec => String(spec.itemId) === String(itemId))
  const target = index + Math.sign(direction)
  if (index < 0 || !items[target] || !direction) return renderItems
  const moved = { ...items[index], workspace: items[target].workspace }
  items.splice(index, 1)
  items.splice(target, 0, moved)
  return renderItems.some(isWallMarkerSpec) ? addWorkspaceMarkers(items) : items
}

export function autoReorderRate(distance, viewportWidth) {
  return Math.sign(distance) * Math.min(5, Math.max(0, Math.abs(distance) - 20) / Math.max(1, viewportWidth * 0.35) * 5)
}
